import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  Building2,
  ChevronRight,
  Clock3,
  Download,
  Eye,
  File,
  Folder,
  FolderOpen,
  Loader2,
  Menu,
  PanelRightClose,
  PanelRightOpen,
  RotateCcw,
  Search,
  Trash2,
} from "lucide-react";
import { AppShell } from "components/dashboard/app-shell";
import {
  DocumentFileManager,
  type CuboneFile,
} from "components/documents/DocumentFileManager";
import { DocumentConsultationPanel } from "../components/documents/DocumentConsultationPanel";
import { DocumentReader } from "components/documents/DocumentReader";
import { DocumentActionDialogs } from "components/documents/DocumentActionDialogs";
import { OpenClientDossierDialog } from "components/documents/OpenClientDossierDialog";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "components/ui/sheet";
import { useUser } from "contexts/UserContext";
import { useDocumentUploads } from "contexts/DocumentUploadContext";
import { useAgentPageContext } from "contexts/AgentPageContext";
import {
  deleteDocumentNode,
  downloadDocumentFile,
  getDocumentNode,
  listDocumentNodes,
  listDocumentSpaces,
  moveDocumentNode,
  renameDocumentNode,
} from "services/documents";
import type {
  DocumentCapabilities,
  DocumentNode,
  DocumentSpace,
} from "types/documents";
import { buildDocumentPath, parseDocumentPath } from "utils/documentNavigation";
import {
  parseContentDispositionFilename,
  saveBlobAs,
} from "utils/downloadHelpers";
import "components/documents/documents-responsive.css";

const RECENT_KEY = "hexa:recent-document-spaces";
function readRecentIds(): number[] {
  try {
    const value = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
    return Array.isArray(value)
      ? value.filter(Number.isSafeInteger).slice(0, 5)
      : [];
  } catch {
    return [];
  }
}
function nodeToFile(node: DocumentNode): CuboneFile {
  const logicalPath = node.logical_path || node.name;
  return {
    _id: node.id,
    name: node.name,
    path: logicalPath.startsWith("/") ? logicalPath : `/${logicalPath}`,
    isDirectory: node.node_type === "FOLDER",
    updatedAt: node.created_at,
    uploadedBy: node.uploaded_by,
    size: node.file_size ?? undefined,
    indexStatus: node.index_status,
    indexReason: node.index_reason,
    indexReasonCode: node.index_reason_code,
    indexUpdatedAt: node.index_updated_at,
    indexedAt: node.indexed_at,
  };
}
function flattenFolders(nodes: DocumentNode[], prefix = ""): CuboneFile[] {
  return nodes.flatMap((node) => {
    if (node.node_type !== "FOLDER") return [];
    const path = `${prefix}/${node.name}`;
    return [
      { _id: node.id, name: node.name, path, isDirectory: true },
      ...flattenFolders(node.children ?? [], path),
    ];
  });
}

export default function DocumentsPage() {
  const { user, loading: userLoading } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const target = useMemo(
    () => parseDocumentPath(location.pathname),
    [location.pathname],
  );
  const [spaces, setSpaces] = useState<DocumentSpace[]>([]);
  const [activeSpace, setActiveSpace] = useState<DocumentSpace | null>(null);
  const [folder, setFolder] = useState<CuboneFile | null>(null);
  const [selected, setSelected] = useState<CuboneFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [recentIds, setRecentIds] = useState<number[]>(readRecentIds);
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  const [spacesDrawerOpen, setSpacesDrawerOpen] = useState(false);
  const [openDossier, setOpenDossier] = useState(false);
  const [managerState, setManagerState] = useState({
    loading: true,
    error: false,
    empty: false,
  });
  const [managerRevision, setManagerRevision] = useState(0);
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveSpaceId, setMoveSpaceId] = useState<number | null>(null);
  const [moveParentId, setMoveParentId] = useState<number | null>(null);
  const [renameOpen, setRenameOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [actionError, setActionError] = useState("");
  const [destinationFolders, setDestinationFolders] = useState<CuboneFile[]>(
    [],
  );
  const [moving, setMoving] = useState(false);
  const uploads = useDocumentUploads();
  const { setDocumentContext } = useAgentPageContext();
  const [readerNode, setReaderNode] = useState<DocumentNode | null>(null);
  const [readerSiblings, setReaderSiblings] = useState<DocumentNode[]>([]);
  const restoreManagerFocus = useRef(false);

  useEffect(() => {
    if (!activeSpace) return;
    setDocumentContext({
      suggestedSpaceId: activeSpace.id,
      suggestedSpaceName: activeSpace.name,
      suggestedSpaceType: activeSpace.space_type,
      folderId: folder?._id,
      nodeId: readerNode?.id,
    });
    return () => setDocumentContext(null);
  }, [activeSpace, folder?._id, readerNode?.id, setDocumentContext]);

  const selectSpace = useCallback(
    (space: DocumentSpace, replace = false) => {
      setActiveSpace(space);
      setFolder(null);
      setSelected([]);
      setSpacesDrawerOpen(false);
      setRecentIds((current) => {
        const next = [
          space.id,
          ...current.filter((id) => id !== space.id),
        ].slice(0, 5);
        localStorage.setItem(RECENT_KEY, JSON.stringify(next));
        return next;
      });
      navigate(buildDocumentPath({ kind: "space", spaceId: space.id }), {
        replace,
      });
    },
    [navigate],
  );
  useEffect(() => {
    // Le viste operative cambiano soltanto la superficie di consultazione.
    // Se gli spazi sono gia disponibili, non attivare il loading globale:
    // smonterebbe Cubone e causerebbe un secondo caricamento dei nodi.
    if (target?.kind === "view" && spaces.length > 0) return;

    // Anche la selezione di un dossier aggiorna l'URL dopo avere gia impostato
    // activeSpace. Ricaricare qui gli spazi attiverebbe il loader globale,
    // smonterebbe Cubone e ne farebbe ripartire il caricamento dei nodi.
    // Gli spazi sono gia in memoria, quindi per una route di spazio basta
    // sincronizzare lo stato con l'URL.
    if (target?.kind === "space" && spaces.length > 0) {
      const chosen = spaces.find((space) => space.id === target.spaceId);
      if (!chosen) {
        setActiveSpace(null);
        setError("Dossier non trovato o non accessibile.");
        return;
      }
      setError(null);
      setActiveSpace(chosen);
      setFolder(null);
      setSelected([]);
      setReaderNode(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setReaderNode(null);
    listDocumentSpaces()
      .then(async (items) => {
        if (cancelled) return;
        setSpaces(items);
        if (target?.kind === "view") return;
        const requestedId =
          target && "spaceId" in target ? target.spaceId : undefined;
        const chosen =
          items.find((space) => space.id === requestedId) ??
          items.find((space) => space.space_type === "GENERAL") ??
          null;
        if (!chosen) {
          setError("Archivio aziendale non disponibile.");
          return;
        }
        setActiveSpace(chosen);
        if (target?.kind === "home")
          navigate(buildDocumentPath({ kind: "space", spaceId: chosen.id }), {
            replace: true,
          });
        if (requestedId && chosen.id !== requestedId) {
          setError("Dossier non trovato o non accessibile.");
          return;
        }
        if (target?.kind === "folder") {
          const node = await getDocumentNode(target.folderId);
          if (node.space_id !== chosen.id || node.node_type !== "FOLDER")
            throw new Error("invalid-folder");
          setFolder(nodeToFile(node));
          setSelected([]);
        } else if (target?.kind === "file") {
          const node = await getDocumentNode(target.fileId);
          if (node.space_id !== chosen.id || node.node_type !== "FILE")
            throw new Error("invalid-file");
          setSelected([nodeToFile(node)]);
          setReaderNode(node);
          if (node.parent)
            setFolder(nodeToFile(await getDocumentNode(node.parent)));
          else setFolder(null);
        }
      })
      .catch(() => {
        if (!cancelled) setError("Impossibile caricare i documenti.");
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);
  useEffect(() => {
    if (loading || readerNode || !restoreManagerFocus.current) return;
    const manager = document.querySelector<HTMLElement>("[data-cubone-root]");
    if (!manager) return;
    restoreManagerFocus.current = false;
    manager.focus();
  }, [loading, readerNode]);
  useEffect(() => {
    if (!moveOpen || moveSpaceId === null) return;
    listDocumentNodes(moveSpaceId)
      .then((nodes) => setDestinationFolders(flattenFolders(nodes)))
      .catch(() => setDestinationFolders([]));
  }, [moveOpen, moveSpaceId]);
  useEffect(() => {
    if (!readerNode) {
      setReaderSiblings([]);
      return;
    }
    let cancelled = false;
    listDocumentNodes(readerNode.space_id, { parentId: readerNode.parent })
      .then((nodes) => {
        if (!cancelled)
          setReaderSiblings(nodes.filter((node) => node.node_type === "FILE"));
      })
      .catch(() => {
        if (!cancelled) setReaderSiblings([readerNode]);
      });
    return () => {
      cancelled = true;
    };
  }, [readerNode?.id, readerNode?.parent, readerNode?.space_id]);

  useEffect(() => {
    if (target?.kind !== "view" || spaces.length === 0) return;
    setError(null);
    setActiveSpace(
      (current) =>
        current ??
        spaces.find((space) => space.space_type === "GENERAL") ??
        null,
    );
  }, [spaces, target]);

  const general = spaces.find((space) => space.space_type === "GENERAL");
  const dossiers = spaces.filter((space) => space.space_type === "ENTITY");
  const filteredDossiers = dossiers.filter((space) =>
    space.name.toLowerCase().includes(query.toLowerCase()),
  );
  const recent = recentIds
    .map((id) => dossiers.find((space) => space.id === id))
    .filter(Boolean) as DocumentSpace[];
  const selectedFile = selected.length === 1 ? selected[0] : null;
  const refreshManager = () => setManagerRevision((value) => value + 1);
  const renameSelected = async (name: string) => {
    if (!selectedFile) return;
    setMoving(true);
    setActionError("");
    try {
      setSelected([
        nodeToFile(await renameDocumentNode(selectedFile._id, name)),
      ]);
      setRenameOpen(false);
      refreshManager();
    } catch {
      setActionError("Rinomina non riuscita. Riprova.");
    } finally {
      setMoving(false);
    }
  };
  const deleteSelected = async () => {
    if (!selectedFile) return;
    setMoving(true);
    setActionError("");
    try {
      await deleteDocumentNode(selectedFile._id);
      setSelected([]);
      setDeleteOpen(false);
      refreshManager();
    } catch {
      setActionError("Eliminazione non riuscita. Riprova.");
    } finally {
      setMoving(false);
    }
  };
  const downloadFile = async (file: Pick<CuboneFile, "_id" | "name" | "isDirectory">) => {
    if (file.isDirectory) return;
    const response = await downloadDocumentFile(file._id);
    saveBlobAs(
      response.data,
      parseContentDispositionFilename(
        response.headers?.["content-disposition"],
      ) || file.name,
    );
  };
  const downloadSelected = async () => {
    if (!selectedFile) return;
    await downloadFile(selectedFile);
  };
  const downloadReaderNode = async () => {
    if (!readerNode) return;
    await downloadFile(nodeToFile(readerNode));
  };
  const openSelected = () => {
    if (selectedFile && !selectedFile.isDirectory && activeSpace)
      navigate(
        buildDocumentPath({
          kind: "file",
          spaceId: activeSpace.id,
          fileId: selectedFile._id,
        }),
      );
  };
  const closeReader = () => {
    restoreManagerFocus.current = true;
    if (activeSpace)
      navigate(
        folder
          ? buildDocumentPath({
              kind: "folder",
              spaceId: activeSpace.id,
              folderId: folder._id,
            })
          : buildDocumentPath({ kind: "space", spaceId: activeSpace.id }),
        { replace: true },
      );
  };
  const readerIndex = readerNode
    ? readerSiblings.findIndex((node) => node.id === readerNode.id)
    : -1;
  const navigateReader = (offset: -1 | 1) => {
    const next = readerSiblings[readerIndex + offset];
    if (next)
      navigate(
        buildDocumentPath({
          kind: "file",
          spaceId: next.space_id,
          fileId: next.id,
        }),
      );
  };
  const confirmMove = async () => {
    if (!selectedFile || moveSpaceId === null || !activeSpace) return;
    setMoving(true);
    setActionError("");
    try {
      await moveDocumentNode(selectedFile._id, moveParentId, moveSpaceId);
      setMoveOpen(false);
      setSelected([]);
      refreshManager();
      navigate(buildDocumentPath({ kind: "space", spaceId: activeSpace.id }), {
        replace: true,
      });
    } catch {
      setActionError("Spostamento non riuscito. Riprova.");
    } finally {
      setMoving(false);
    }
  };

  if (userLoading)
    return (
      <div className="min-h-screen grid place-items-center">Caricamento...</div>
    );
  if (!user && !localStorage.getItem("accessToken"))
    return <Navigate to="/login" replace />;
  const spacesPanel = (
    <SpacesPanel
      general={general}
      activeSpace={activeSpace}
      query={query}
      loading={loading}
      filteredDossiers={filteredDossiers}
      recent={recent}
      onQueryChange={setQuery}
      onSelect={selectSpace}
      onOpenDossier={() => {
        setSpacesDrawerOpen(false);
        setOpenDossier(true);
      }}
    />
  );
  const detailsPanel = (
    <div className="space-y-2">
      {selectedFile && !selectedFile.isDirectory && (
        <Button className="w-full" size="sm" onClick={openSelected}>
          <Eye className="mr-1 h-4 w-4" />
          Apri anteprima
        </Button>
      )}
      <Details
        file={selectedFile}
        capabilities={activeSpace?.capabilities}
        uploads={uploads}
        onOpen={openSelected}
        onRename={() => {
          setActionError("");
          setRenameOpen(true);
        }}
        onDelete={() => {
          setActionError("");
          setDeleteOpen(true);
        }}
        onDownload={downloadSelected}
        onMove={() => {
          setActionError("");
          setMoveSpaceId(activeSpace?.id ?? null);
          setMoveParentId(null);
          setMoveOpen(true);
        }}
      />
    </div>
  );

  return (
    <AppShell currentUser={user}>
      <div
        className="documents-page flex min-h-0 min-w-0 flex-col px-3 py-3 pb-24 sm:px-5 sm:py-4 sm:pb-24 lg:h-[calc(100dvh-3rem)] lg:min-h-[620px]"
        data-testid="documents-page"
      >
        <DocumentConsultationPanel
          spaces={spaces}
          activeSpace={activeSpace}
          operationalView={target?.kind === "view" ? target.view : undefined}
          userKey={String(user?.id ?? user?.username ?? "anonymous")}
          username={user?.username}
          onOpen={navigate}
          onViewChange={(view) =>
            navigate(buildDocumentPath({ kind: "view", view }))
          }
          onDocumentRestored={(node) => {
            if (node.space_id === activeSpace?.id) refreshManager();
          }}
        />
        <header className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold">Documenti</h1>
            <p className="text-sm text-[var(--hexa-text-muted)]">
              Archivio aziendale e dossier cliente
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap gap-2">
            <Button
              id="documents-spaces-trigger"
              className="lg:hidden"
              variant="outline"
              size="sm"
              onClick={() => setSpacesDrawerOpen(true)}
              aria-expanded={spacesDrawerOpen}
            >
              <Menu className="mr-1 h-4 w-4" /> Spazi
            </Button>
            <Button
              id="documents-details-trigger"
              className="xl:hidden"
              variant="outline"
              size="sm"
              onClick={() => setDetailsDrawerOpen(true)}
              aria-expanded={detailsDrawerOpen}
            >
              <PanelRightOpen className="mr-1 h-4 w-4" /> Dettagli
            </Button>
            <Button
              className="hidden xl:inline-flex"
              variant="outline"
              size="sm"
              onClick={() => setDetailsOpen((value) => !value)}
              aria-expanded={detailsOpen}
            >
              {detailsOpen ? (
                <PanelRightClose className="mr-1 h-4 w-4" />
              ) : (
                <PanelRightOpen className="mr-1 h-4 w-4" />
              )}{" "}
              Dettagli
            </Button>
          </div>
        </header>
        <div className="grid min-h-0 min-w-0 flex-1 gap-4 lg:grid-cols-[210px_minmax(0,1fr)] xl:grid-cols-[230px_minmax(0,1fr)]">
          <aside
            className="hidden min-h-0 overflow-auto rounded-xl border bg-[var(--hexa-surface)] p-3 lg:block"
            aria-label="Spazi documentali"
          >
            {spacesPanel}
          </aside>
          <section
            className={`grid min-h-0 min-w-0 grid-cols-1 gap-4 ${detailsOpen ? "xl:grid-cols-[minmax(0,1fr)_280px]" : ""}`}
          >
            <div className="flex min-h-[520px] min-w-0 flex-col overflow-hidden rounded-xl border bg-[var(--hexa-surface)] p-3 lg:min-h-0">
              <nav
                aria-label="Percorso documenti"
                className="mb-3 flex min-w-0 items-center gap-1 overflow-hidden text-sm"
              >
                <button
                  onClick={() => activeSpace && selectSpace(activeSpace)}
                  className="truncate font-medium hover:underline"
                >
                  {activeSpace?.name || "Documenti"}
                </button>
                {folder && (
                  <>
                    <ChevronRight className="h-4 w-4 shrink-0" />
                    <span className="truncate">{folder.name}</span>
                  </>
                )}
              </nav>
              {loading ? (
                <State
                  icon={<Loader2 className="animate-spin" />}
                  title="Caricamento documenti..."
                />
              ) : error ? (
                <State
                  title={error}
                  action={
                    <Button
                      variant="outline"
                      onClick={() => window.location.reload()}
                    >
                      Riprova
                    </Button>
                  }
                />
              ) : activeSpace ? (
                <>
                  <div className="min-h-0 min-w-0 flex-1 overflow-hidden">
                    <DocumentFileManager
                      space={activeSpace}
                      currentFolder={folder}
                      selectedFiles={selected}
                      capabilities={activeSpace.capabilities}
                      mode="full"
                      externalRevision={managerRevision}
                      onLoadStateChange={setManagerState}
                      onFolderChange={(next) => {
                        setFolder(next);
                        navigate(
                          next
                            ? buildDocumentPath({
                                kind: "folder",
                                spaceId: activeSpace.id,
                                folderId: next._id,
                              })
                            : buildDocumentPath({
                                kind: "space",
                                spaceId: activeSpace.id,
                              }),
                        );
                      }}
                      onSelectionChange={setSelected}
                      onFileOpen={(file) =>
                        navigate(
                          buildDocumentPath(
                            file.isDirectory
                              ? {
                                  kind: "folder",
                                  spaceId: activeSpace.id,
                                  folderId: file._id,
                                }
                              : {
                                  kind: "file",
                                  spaceId: activeSpace.id,
                                  fileId: file._id,
                                },
                          ),
                        )
                      }
                    />
                  </div>
                  {managerState.empty && (
                    <span className="sr-only">
                      Archivio vuoto. Usa Carica o Nuova cartella.
                    </span>
                  )}
                </>
              ) : null}
            </div>
            {detailsOpen && (
              <div className="hidden min-h-0 xl:block">{detailsPanel}</div>
            )}
          </section>
        </div>
        <Sheet
          open={spacesDrawerOpen}
          onOpenChange={(open) => {
            setSpacesDrawerOpen(open);
            if (!open) requestAnimationFrame(() => document.getElementById("documents-spaces-trigger")?.focus({ preventScroll: true }));
          }}
        >
          <SheetContent
            side="left"
            className="document-sheet w-full sm:w-[320px] max-w-none h-[100dvh] bg-[var(--hexa-surface)] p-4 lg:hidden"
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              document.getElementById("documents-spaces-trigger")?.focus({ preventScroll: true });
            }}
          >
            <SheetTitle>Spazi documentali</SheetTitle>
            <SheetDescription>
              Scegli l'archivio aziendale o un dossier cliente.
            </SheetDescription>
            <Button variant="outline" onClick={() => setSpacesDrawerOpen(false)} aria-label="Chiudi spazi documentali">Chiudi</Button>
            <div className="min-h-0 flex-1 overflow-auto pt-2">
              {spacesPanel}
            </div>
          </SheetContent>
        </Sheet>
        <Sheet
          open={detailsDrawerOpen}
          onOpenChange={(open) => {
            setDetailsDrawerOpen(open);
            if (!open) requestAnimationFrame(() => document.getElementById("documents-details-trigger")?.focus({ preventScroll: true }));
          }}
        >
          <SheetContent
            side="right"
            className="document-sheet w-full max-w-full h-[100dvh] bg-[var(--hexa-surface)] p-4 sm:w-[360px] sm:max-w-[360px] xl:hidden"
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              document.getElementById("documents-details-trigger")?.focus({ preventScroll: true });
            }}
          >
            <SheetTitle className="sr-only">Dettagli documento</SheetTitle>
            <SheetDescription className="sr-only">
              Informazioni e azioni del documento selezionato.
            </SheetDescription>
            <Button variant="outline" className="shrink-0 self-end" onClick={() => setDetailsDrawerOpen(false)} aria-label="Chiudi dettagli documento">Chiudi</Button>
            <div className="min-h-0 flex-1 overflow-auto">{detailsPanel}</div>
          </SheetContent>
        </Sheet>
        {openDossier && <OpenClientDossierDialog
          open={openDossier}
          onOpenChange={setOpenDossier}
          onSelect={(space) => {
            setSpaces((current) => [...current.filter((item) => item.id !== space.id), space]);
            setQuery("");
            setError(null);
            setReaderNode(null);
            selectSpace(space);
          }}
        />}
        {readerNode && (
          <DocumentReader
            node={readerNode}
            canDownload={Boolean(activeSpace?.capabilities.download)}
            hasPrevious={readerIndex > 0}
            hasNext={
              readerIndex >= 0 && readerIndex < readerSiblings.length - 1
            }
            onClose={closeReader}
            onDownload={downloadReaderNode}
            onPrevious={() => navigateReader(-1)}
            onNext={() => navigateReader(1)}
          />
        )}
        {selectedFile && (
          <DocumentActionDialogs
            file={selectedFile}
            spaces={spaces}
            destinationFolders={destinationFolders}
            renameOpen={renameOpen}
            deleteOpen={deleteOpen}
            moveOpen={moveOpen}
            moveSpaceId={moveSpaceId}
            moveParentId={moveParentId}
            loading={moving}
            error={actionError}
            onRenameOpenChange={setRenameOpen}
            onDeleteOpenChange={setDeleteOpen}
            onMoveOpenChange={setMoveOpen}
            onMoveSpaceChange={(spaceId) => {
              setMoveSpaceId(spaceId);
              setMoveParentId(null);
            }}
            onMoveParentChange={setMoveParentId}
            onRename={(name) => void renameSelected(name)}
            onDelete={() => void deleteSelected()}
            onMove={() => void confirmMove()}
          />
        )}
      </div>
    </AppShell>
  );
}

function Details({
  file,
  capabilities,
  uploads,
  onOpen,
  onRename,
  onDelete,
  onDownload,
  onMove,
}: {
  file: CuboneFile | null;
  capabilities?: DocumentCapabilities;
  uploads: ReturnType<typeof useDocumentUploads>;
  onOpen: () => void;
  onRename: () => void;
  onDelete: () => void;
  onDownload: () => void;
  onMove: () => void;
}) {
  const labels: Record<string, string> = {
    pending: "In attesa",
    indexing: "In corso",
    indexed: "Indicizzato",
    unsupported: "Formato non supportato",
    extraction_failed: "Estrazione testo non riuscita",
    too_large: "File troppo grande",
    failed: "Indicizzazione non riuscita",
  };
  return (
    <aside
      aria-label="Dettagli documento"
      className="min-w-0 overflow-auto break-words rounded-xl border bg-[var(--hexa-surface)] p-4"
    >
      <h2 className="font-semibold">Dettagli</h2>
      {file ? (
        <div className="mt-4 space-y-3 text-sm">
          <div className="flex items-center gap-2">
            <File className="h-5 w-5 shrink-0" />
            <strong className="min-w-0 break-words [overflow-wrap:anywhere]">{file.name}</strong>
          </div>
          <p>{file.isDirectory ? "Cartella" : "Documento"}</p>
          {file.size !== undefined && (
            <p>{file.size.toLocaleString("it-IT")} byte</p>
          )}
          {file.uploadedBy && <p>Caricato da: {file.uploadedBy}</p>}
          {file.indexStatus && (
            <div>
              <p>AI: {labels[file.indexStatus] ?? file.indexStatus}</p>
              {file.indexReason && (
                <p className="text-amber-700">{file.indexReason}</p>
              )}
            </div>
          )}
          <div className="flex flex-wrap gap-2 pt-2">
            {capabilities?.download && !file.isDirectory && (
              <Button size="sm" variant="outline" onClick={onDownload}>
                <Download className="mr-1 h-4 w-4" />
                Scarica
              </Button>
            )}
            {capabilities?.rename && (
              <Button size="sm" variant="outline" onClick={onRename}>
                Rinomina
              </Button>
            )}
            {capabilities?.move && (
              <Button size="sm" variant="outline" onClick={onMove}>
                Sposta
              </Button>
            )}
            {capabilities?.delete && (
              <Button size="sm" variant="outline" onClick={onDelete}>
                <Trash2 className="mr-1 h-4 w-4" />
                Elimina
              </Button>
            )}
          </div>
        </div>
      ) : (
        <p className="mt-4 text-sm text-[var(--hexa-text-muted)]">
          Seleziona un file per visualizzarne i dettagli.
        </p>
      )}
      <div className="mt-8 border-t pt-4">
        <p className="text-xs font-semibold uppercase text-[var(--hexa-text-muted)]">
          Coda upload
        </p>
        {uploads.items.length === 0 ? (
          <p className="mt-3 text-sm text-[var(--hexa-text-muted)]">
            Nessun caricamento
          </p>
        ) : (
          <div className="mt-3 space-y-3">
            {uploads.items.map((item) => (
              <div key={item.id} className="text-xs">
                <div className="flex justify-between gap-2">
                  <span className="truncate">{item.fileName}</span>
                  <span>
                    {item.status === "failed"
                      ? "Errore"
                      : item.status === "completed"
                        ? "Completato"
                        : `${item.progress}%`}
                  </span>
                </div>
                <div className="mt-1 h-1.5 rounded bg-gray-200">
                  <div
                    className="h-full rounded bg-[var(--hexa-purple)]"
                    style={{ width: `${item.progress}%` }}
                  />
                </div>
                <div className="mt-1 flex gap-2">
                  {item.status === "failed" && (
                    <button onClick={() => uploads.retry(item.id)}>
                      <RotateCcw className="inline h-3 w-3" /> Riprova
                    </button>
                  )}
                  {["queued", "uploading", "completing"].includes(
                    item.status,
                  ) && (
                    <button onClick={() => uploads.cancel(item.id)}>
                      Annulla
                    </button>
                  )}
                  {item.status === "completed" && item.nodeId && (
                    <a
                      className="underline"
                      href={buildDocumentPath({
                        kind: "file",
                        spaceId: item.spaceId,
                        fileId: item.nodeId,
                      })}
                    >
                      Apri documento
                    </a>
                  )}
                  {["completed", "cancelled"].includes(item.status) && (
                    <button onClick={() => uploads.dismiss(item.id)}>
                      Rimuovi
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </aside>
  );
}
function SpacesPanel({
  general,
  activeSpace,
  query,
  loading,
  filteredDossiers,
  recent,
  onQueryChange,
  onSelect,
  onOpenDossier,
}: {
  general?: DocumentSpace;
  activeSpace: DocumentSpace | null;
  query: string;
  loading: boolean;
  filteredDossiers: DocumentSpace[];
  recent: DocumentSpace[];
  onQueryChange: (value: string) => void;
  onSelect: (space: DocumentSpace) => void;
  onOpenDossier: () => void;
}) {
  return (
    <>
      <SpaceButton
        icon={<Building2 />}
        label={general?.name || "Archivio aziendale"}
        active={general?.id === activeSpace?.id}
        disabled={!general}
        onClick={() => general && onSelect(general)}
      />
      <div className="mt-5">
        <p className="mb-2 text-xs font-semibold uppercase text-[var(--hexa-text-muted)]">
          Dossier cliente
        </p>
        <Button type="button" variant="outline" size="sm" className="mb-3 w-full whitespace-normal" onClick={onOpenDossier}>
          <FolderOpen className="mr-1 h-4 w-4 shrink-0" />Apri dossier cliente
        </Button>
        <div className="relative mb-2">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-[var(--hexa-text-muted)]" />
          <Input
            aria-label="Cerca dossier cliente"
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            className="pl-8"
            placeholder="Cerca dossier"
          />
        </div>
        {filteredDossiers.map((space) => (
          <SpaceButton
            key={space.id}
            icon={<Folder />}
            label={space.name}
            active={space.id === activeSpace?.id}
            onClick={() => onSelect(space)}
          />
        ))}
        {!loading && filteredDossiers.length === 0 && (
          <p className="p-2 text-xs text-[var(--hexa-text-muted)]">
            Nessun dossier trovato.
          </p>
        )}
      </div>
      {recent.length > 0 && (
        <div className="mt-5">
          <p className="mb-2 flex items-center gap-1 text-xs font-semibold uppercase text-[var(--hexa-text-muted)]">
            <Clock3 className="h-3 w-3" /> Recenti
          </p>
          {recent.map((space) => (
            <SpaceButton
              key={space.id}
              icon={<Folder />}
              label={space.name}
              active={space.id === activeSpace?.id}
              onClick={() => onSelect(space)}
            />
          ))}
        </div>
      )}
    </>
  );
}
function SpaceButton({
  icon,
  label,
  active,
  disabled,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`mb-1 flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm ${active ? "bg-[var(--hexa-sidebar-item-active)] font-medium" : "hover:bg-[var(--hexa-sidebar-item-hover)]"}`}
    >
      <span className="shrink-0 [&>svg]:h-4 [&>svg]:w-4">{icon}</span>
      <span title={label} className="min-w-0 truncate">{label}</span>
    </button>
  );
}
function State({
  icon,
  title,
  action,
}: {
  icon?: React.ReactNode;
  title: string;
  action?: React.ReactNode;
}) {
  return (
    <div
      role={
        title.includes("Impossibile") || title.includes("non")
          ? "alert"
          : "status"
      }
      className="grid min-h-[300px] place-content-center justify-items-center gap-3 text-center text-sm text-[var(--hexa-text-muted)]"
    >
      {icon}
      <p>{title}</p>
      {action}
    </div>
  );
}
